import React, { useRef, useEffect } from 'react';
import styles from './MobileMenu.module.scss';
import { useRouter } from 'next/router';
import { useAppContext } from 'Providers/App';
import Link from 'next/link';
import Carot from 'Images/brand-icons/angle-down.svg';
import Button from 'Nori/Button';
import useKeydownHandler from 'Utils/hooks/useKeyboardHandler';
import useTrapFocus from 'Utils/hooks/useTrapFocus';
import useMenuFocusLastTrigger from 'Utils/hooks/useMenuFocusLastTrigger';
import {
  useGigyaReady,
  createHandleOpenLogin,
  PATIENT_HUB_CONFIG,
} from 'Utils/hooks/useGigyaService';
import { MOBILE_MENU_CONTENT } from 'Custom/Header/Header.utils';
import { finderHandlerClick } from 'Custom/Finder/utils';
import { UI_ELEMENTS } from 'Providers/App/elements';

const HEADER_MOBILE_HEIGHT = 82;
const {
  modalHealthcare: modalHealthcareId,
  modalISI: modalISIId,
  mobileMainMenu: mobileMainMenuId,
  mobileMenuOpen: mobileMenuOpenId,
} = UI_ELEMENTS;

const MenuItem = ({ href, label, id, submenu = null, target = null }) => {
  let icon = null;
  const { setMobileActiveMenu, updateElementState } = useAppContext();

  const openSection = (id) => {
    updateElementState(mobileMainMenuId, true);
    setMobileActiveMenu(id);
  };

  if (target === '_blank') {
    icon = <ExternalLink />;
  }

  return href && !submenu ? (
    <Link
      className={styles.mobileItem}
      href={href}
      target={target}
      onClick={() => updateElementState(mobileMenuOpenId, false)}
      data-design-type="clickAction"
      data-design-category="top_nav_vd"
      data-design-action="Link"
      data-design-label={label}
      data-design-nori_id="Navigation"
      data-design-module_name="Navigation"
      data-design-module_no="00"
      data-qa-tag="mobile-menu-link"
    >
      <span
        data-design-type="clickAction"
        data-design-category="top_nav_vd"
        data-design-action="Link"
        data-design-label={label}
        data-design-nori_id="Navigation"
        data-design-module_name="Navigation"
        data-design-module_no="00"
        data-qa-tag="mobile-menu-text"
      >
        {label}
      </span>
      {icon}
    </Link>
  ) : (
    <button
      onClick={() => openSection(id)}
      className={styles.mobileItem}
      data-menu-item-id={id}
      data-design-type="clickAction"
      data-design-category="top_nav_vd"
      data-design-action="Expand"
      data-design-label={label}
      data-design-nori_id="Navigation"
      data-design-module_name="Navigation"
      data-design-module_no="00"
      data-qa-tag="mobile-menu-item-button"
    >
      {label} {submenu && <Carot />}
    </button>
  );
};

const MobileMenu = ({
  basic = false,
  loginButton = false,
  logoutButton = false,
  content = MOBILE_MENU_CONTENT,
}) => {
  const { mainNavList, secondaryNavList } = content;
  const { pathname, push: routerPush } = useRouter();
  const isGigyaReady = useGigyaReady();
  const {
    getElementState,
    focusTrigger,
    updateElementState,
    getElementClickHandler,
    mobileActiveMenu,
    registerTrigger,
  } = useAppContext();

  const wrapperRef = useRef(null);
  const [mobileMenuOpen, mobileMainMenu] = getElementState(
    mobileMenuOpenId,
    mobileMainMenuId
  );
  const modalISIHandler = getElementClickHandler(modalISIId);
  const modalHealthcareHandler = getElementClickHandler(modalHealthcareId);
  const handleOpenLogin = createHandleOpenLogin();

  const resizeObserverFn = () => {
    const height = window.innerHeight - HEADER_MOBILE_HEIGHT;

    if (wrapperRef.current) {
      wrapperRef.current.style.height = `${height}px`;
    }
  };

  const closeMobileMenu = () => {
    updateElementState(mobileMenuOpenId, false);
  };

  const handleLogout = () => {
    if (isGigyaReady) {
      window.gigya.accounts.logout();
      closeMobileMenu();
      routerPush(PATIENT_HUB_CONFIG.REGISTRATION_URL);
    }
  };

  useKeydownHandler(() => {
    closeMobileMenu();
    const menuButton = document.querySelector('#open-mobile-menu-button');
    registerTrigger(UI_ELEMENTS.menuMobile, menuButton);
    focusTrigger(UI_ELEMENTS.menuMobile);
    document.body.classList.toggle('overflowHidden', false);
  }, 'Escape');

  useTrapFocus(wrapperRef, mobileMenuOpen, [mobileMainMenu]);

  useMenuFocusLastTrigger(wrapperRef, mobileMenuOpen);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(resizeObserverFn);
    setTimeout(resizeObserverFn, 400);
    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, []);

  const menu = mainNavList.map((item, index) => {
    const { submenu = null } = item;
    const isActive =
      pathname === item.href ||
      (submenu && submenu.some((i) => i.href === pathname));

    const liClassName = `${isActive ? styles.active : ''}`;

    return (
      <li
        key={`li-${index}`}
        className={liClassName}
        data-qa-tag="nav-list-item"
      >
        <MenuItem {...item} />
      </li>
    );
  });

  const subMenu = mainNavList.map((item, index) => {
    const { submenu = null, id } = item;

    if (mobileActiveMenu === id && submenu) {
      const isActive =
        pathname === item.link ||
        (submenu && submenu.some((i) => i.link === pathname));

      const liClassName = `${isActive ? styles.active : ''}`;

      return (
        <ul key={`ul-${index}`} data-qa-tag="mobile-nav-lists">
          {submenu.map((subitem, subindex) => (
            <li
              key={`li-${index}-${subindex}`}
              className={
                subitem.href === pathname ? `${styles.activeSubItem}` : ''
              }
              data-qa-tag="mobile-nav-list-item"
            >
              <MenuItem isSubItem {...subitem} />
            </li>
          ))}
        </ul>
      );
    }

    return null;
  });

  return (
    <div
      ref={wrapperRef}
      className={styles.mobileMenu}
      data-qa-tag="mobile-menu-wrapper"
    >
      {mobileMainMenu ? (
        <div className={styles.subMenu} data-qa-tag="mobile-nav-submenu">
          <button
            id="back-button"
            className={styles.backButton}
            data-design-type="clickAction"
            data-design-category="top_nav_vd"
            data-design-action="Close"
            data-design-nori_id="Navigation"
            data-design-module_name="Navigation"
            data-design-module_no="00"
            data-qa-tag="mobile-nav-back-button"
            onClick={(event) => {
              registerTrigger(UI_ELEMENTS.menuMobile, event.currentTarget);
              updateElementState(mobileMainMenuId, false);
            }}
          >
            <Carot data-qa-tag="mobile-back-button-image" /> Back
          </button>
          <div
            className={styles.subNavWrapper}
            data-qa-tag="mobile-sub-nav-wrapper"
          >
            <div className={styles.childItems} data-qa-tag="mobile-child-items">
              {subMenu}
              <div
                className={styles.ctaBlockSub}
                data-qa-tag="mobile-button-wrapper"
              >
                <Button
                  type="secondary2"
                  size="medium"
                  onClick={(e) => {
                    closeMobileMenu();
                    if (secondaryNavList.findDoctor['data-custom-redirect']) {
                      finderHandlerClick(e);
                    }
                  }}
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Button"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-findaDoctor-button"
                  href="#find-a-doctor"
                  {...secondaryNavList.findDoctor}
                />

                <Button
                  onClick={() => closeMobileMenu()}
                  type="outlineSecondary2"
                  size="medium"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Button"
                  data-design-label="My NEXPLANON"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  href="/enrollment/"
                  data-qa-tag="mobile-myNexplanon-button"
                >
                  My NEXPLANON
                </Button>
              </div>
            </div>
            <div className={styles.subNavigation} data-qa-tag="mobile-sub-nav">
              <div
                className={styles.subitemList}
                data-qa-tag="mobile-sub-item-list"
              >
                <button
                  data-trigger-mobile-id={modalISIId}
                  onClick={(event) => {
                    closeMobileMenu();
                    modalISIHandler(event);
                  }}
                  className={styles.topNavigation}
                  data-design-category="top_nav_vd"
                  data-design-type="clickAction"
                  data-design-action="Expand"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-importantSafety-button"
                  {...secondaryNavList.importantSafety}
                />
                <Link
                  onClick={() => closeMobileMenu()}
                  href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_pi.pdf"
                  target="_blank"
                  rel="noopener"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Download"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-prescribingInfo-link"
                  {...secondaryNavList.prescribingInfo}
                />

                {!basic && (
                  <button
                    data-trigger-mobile-id={modalHealthcareId}
                    onClick={(event) => {
                      modalHealthcareHandler(event);
                      closeMobileMenu();
                    }}
                    className={styles.topNavigation}
                    data-design-type="clickAction"
                    data-design-category="top_nav_vd"
                    data-design-action="Expand"
                    data-design-nori_id="Navigation"
                    data-design-module_name="Navigation"
                    data-design-module_no="00"
                    data-qa-tag="mobile-healthcare-button"
                    {...secondaryNavList.healthcare}
                  />
                )}
                <Link
                  onClick={() => closeMobileMenu()}
                  href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_ppi.pdf"
                  target="_blank"
                  rel="noopener"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Download"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-patientInfo-link"
                  {...secondaryNavList.patientInfo}
                />
                {!basic && (
                  <Link
                    onClick={() => closeMobileMenu()}
                    href="/es-us"
                    target="_blank"
                    rel="noopener"
                    data-design-type="clickAction"
                    data-design-category="top_nav_vd"
                    data-design-action="Link"
                    data-design-nori_id="Navigation"
                    data-design-module_name="Navigation"
                    data-design-module_no="00"
                    data-qa-tag="mobile-es-language-link"
                    {...secondaryNavList.lang}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.mainSection}>
          {loginButton && (
            <div className={styles.ctaBlock}>
              <Button
                size="medium"
                type="outlineSecondary2"
                data-design-type="clickAction"
                data-design-category="button_vd"
                data-design-action="Button"
                data-design-label="Log In"
                data-design-nori_id="Navigation"
                data-design-module_name="Navigation"
                data-design-module_no="00"
                data-qa-tag="mobile-login-button"
                className={styles.loginButton}
                onClick={() => {
                  closeMobileMenu();
                  handleOpenLogin();
                }}
                disabled={!isGigyaReady}
              >
                Log In
              </Button>
            </div>
          )}

          {logoutButton && (
            <div className={styles.ctaBlock}>
              <Button
                size="medium"
                type="outlineSecondary2"
                data-design-type="clickAction"
                data-design-category="button_vd"
                data-design-action="Button"
                data-design-label="Log Out"
                data-design-nori_id="Navigation"
                data-design-module_name="Navigation"
                data-design-module_no="00"
                data-qa-tag="mobile-logout-button"
                className={styles.loginButton}
                disabled={!isGigyaReady}
                onClick={handleLogout}
              >
                Log Out
              </Button>
            </div>
          )}

          {!basic && (
            <div>
              <ul className={styles.mobileList} data-qa-tag="mobile-menu-list">
                {menu}
              </ul>
              <div className={styles.ctaBlock} data-qa-tag="mobile-cta-wrapper">
                <Button
                  type="secondary2"
                  size="medium"
                  onClick={(e) => {
                    closeMobileMenu();
                    if (secondaryNavList.findDoctor['data-custom-redirect']) {
                      finderHandlerClick(e);
                    }
                  }}
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Button"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-findaDoctor-button"
                  {...secondaryNavList.findDoctor}
                />

                <Button
                  onClick={() => closeMobileMenu()}
                  type="outlineSecondary2"
                  size="medium"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Button"
                  data-design-label="My NEXPLANON"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-myNexplanon-button"
                  href="/enrollment/"
                >
                  My NEXPLANON
                </Button>
              </div>
            </div>
          )}
          <div
            className={styles.subitemList}
            data-qa-tag="mobile-sub-item-list"
          >
            <button
              data-trigger-mobile-id={modalISIId}
              onClick={(event) => {
                closeMobileMenu();
                modalISIHandler(event);
              }}
              className={styles.topNavigation}
              data-design-category="top_nav_vd"
              data-design-type="clickAction"
              data-design-action="Expand"
              data-design-nori_id="Navigation"
              data-design-module_name="Navigation"
              data-design-module_no="00"
              data-qa-tag="mobile-importantSafety-button"
              {...secondaryNavList.importantSafety}
            />
            <Link
              onClick={() => closeMobileMenu()}
              href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_pi.pdf"
              target="_blank"
              rel="noopener"
              data-design-type="clickAction"
              data-design-category="top_nav_vd"
              data-design-action="Download"
              data-design-nori_id="Navigation"
              data-design-module_name="Navigation"
              data-design-module_no="00"
              data-qa-tag="mobile-prescribingInfo-link"
              {...secondaryNavList.prescribingInfo}
            />

            {!basic && (
              <button
                data-trigger-mobile-id={modalHealthcareId}
                onClick={(event) => {
                  modalHealthcareHandler(event);
                  closeMobileMenu();
                }}
                className={styles.topNavigation}
                data-design-type="clickAction"
                data-design-category="top_nav_vd"
                data-design-action="Expand"
                data-design-nori_id="Navigation"
                data-design-module_name="Navigation"
                data-design-module_no="00"
                data-qa-tag="mobile-healthcare-button"
                {...secondaryNavList.healthcare}
              />
            )}
            <Link
              onClick={() => closeMobileMenu()}
              href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_ppi.pdf"
              target="_blank"
              rel="noopener"
              data-design-type="clickAction"
              data-design-category="top_nav_vd"
              data-design-action="Download"
              data-design-nori_id="Navigation"
              data-design-module_name="Navigation"
              data-design-module_no="00"
              data-qa-tag="mobile-patientInfo-link"
              {...secondaryNavList.patientInfo}
            />

            {/* {!basic && (
              <Link
                onClick={() => closeMobileMenu()}
                href="/es-us"
                target="_blank"
                rel="noopener"
                data-design-type="clickAction"
                data-design-category="top_nav_vd"
                data-design-action="Link"
                data-design-nori_id="Navigation"
                data-design-module_name="Navigation"
                data-design-module_no="00"
                data-qa-tag="mobile-es-lang-link"
                {...secondaryNavList.lang}
              />
            )} */}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileMenu;
